/**
 * Waking the planner as soon as a command is saved.
 *
 * A scheduled job wakes the planner once a minute, so a written answer to a
 * question could take up to a minute to appear. The desk therefore pokes the
 * same function directly, after the command row has been written and only
 * then. The poke carries no body, no key and no instruction: it cannot decide
 * anything, it only asks the engine to look now instead of at the next tick.
 * The write boundary is untouched - the browser still writes nothing and
 * orchestrates nothing (ADR-0005, ADR-0009).
 *
 * Everything here is best effort. The scheduled job remains the delivery
 * guarantee, so a poke that is blocked, refused, slow or never sent changes
 * nothing about what the owner eventually sees.
 */

/** Sends closer together than this ride on the run the first one started. */
export const NUDGE_MIN_GAP_MS = 2_000;

/** A poke still unsent by then is abandoned; the next tick does the work. */
export const NUDGE_TIMEOUT_MS = 4_000;

export interface NudgeInput {
  readonly projectId: string;
  readonly content: string;
  readonly now: number;
  /** When the last poke was sent; 0 when none has been. */
  readonly lastAt: number;
}

export interface NudgeDecision {
  readonly send: boolean;
  /** The throttle state to carry forward, whether or not this one is sent. */
  readonly lastAt: number;
}

/**
 * Whether a command just accepted is worth waking the planner for.
 *
 * An organization-wide note is never planned and never answered, so it is
 * never worth a poke. A burst of sends is throttled to one poke, because the
 * run the first poke starts picks up everything written before it reads.
 */
export function nudgeGate(input: NudgeInput): NudgeDecision {
  const worthIt = input.projectId.trim() !== '' && input.content.trim() !== '';
  // A clock that jumped backwards must not silence the desk until it catches up.
  const clear = input.lastAt === 0 || input.now < input.lastAt || input.now - input.lastAt >= NUDGE_MIN_GAP_MS;
  const send = worthIt && clear;
  return { send, lastAt: send ? input.now : input.lastAt };
}

/**
 * Poke the function and forget it. Nothing is awaited, nothing is retried, and
 * every failure - offline, blocked, refused, slow - is swallowed on purpose.
 */
export function sendNudge(
  url: string,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
  timeoutMs: number = NUDGE_TIMEOUT_MS,
): void {
  try {
    // No reply is read and none is needed, so no cross-origin permission is
    // asked for: the poke only has to arrive.
    const pending = fetchImpl(url, {
      method: 'POST',
      mode: 'no-cors',
      cache: 'no-store',
      keepalive: true,
      headers: { 'content-type': 'text/plain' },
      body: '',
      signal: AbortSignal.timeout(timeoutMs),
    });
    void Promise.resolve(pending).catch(() => undefined);
  } catch {
    // A request that refuses even to start is no different from one that fails.
  }
}

/**
 * Send first, poke second. What the caller gets back is exactly what the send
 * returned: a poke that throws is swallowed here, and a send that never
 * resolved is never poked for, so the owner's message can neither be lost nor
 * delayed by it.
 */
export async function withNudge<T>(send: () => Promise<T>, nudge: (result: T) => void): Promise<T> {
  const result = await send();
  try {
    nudge(result);
  } catch {
    // The answer arrives on the next scheduled run instead.
  }
  return result;
}
